import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CheckCircle2, ChevronLeft, ChevronRight, Clock, Flag, ListChecks, XCircle } from 'lucide-react';
import { Chargement, MessageErreur } from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { ContenuProtege } from '../components/ui/ContenuProtege';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { echeanceLongue } from '../lib/affichage-devoirs';
import {
  useCommencerQuiz,
  useQuizApprenant,
  useRepondreQuizQuestion,
  useTerminerQuiz,
  type QuestionQuizApprenant,
  type QuizApprenant,
} from '../lib/useEspaceApprenant';

/**
 * Quiz interactif (écran `quiz` de la maquette validée,
 * `docs/maquette/modules/04-pages-pedagogie-player.js`), TF-454.
 *
 * Le quiz s'ouvre sur son accueil — questions, durée, échéance, règles — sans
 * lancer le temps ; « Commencer » crée l'unique tentative. Une question validée
 * ou passée montre aussitôt la bonne réponse et son explication, qui
 * n'arrivent du serveur qu'à ce moment-là. « Terminer » clôt avant la fin ; le
 * temps écoulé aussi. Le résultat dit le score, la note sur 20 et la
 * correction de chaque question. La note automatique reste provisoire jusqu'à
 * la correction publiée par le formateur, dont la note et le commentaire la
 * remplacent (TF-457). `?retour=` ramène à l'écran d'où il a été
 * ouvert (le lecteur, « Mes formations »), sinon aux devoirs.
 */

const FOND = 'min-h-screen bg-gradient-to-br from-codialis-navy via-codialis-deep to-codialis-blue px-4 py-8 sm:px-6 sm:py-12';
const BOUTON_PRINCIPAL =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60 sm:px-6';
const BOUTON_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60';

function tempsFormate(secondes: number): string {
  const mm = Math.floor(secondes / 60);
  const ss = secondes % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function noteLisible(note: number): string {
  return String(note).replace('.', ',');
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/** Une adresse de l'application seulement : jamais un autre site. */
function retourDemande(valeur: string | null): string {
  return valeur && valeur.startsWith('/') && !valeur.startsWith('//') ? valeur : '/assignments';
}

function EnTete({ quiz, onRetour, chrono }: { quiz: QuizApprenant; onRetour: () => void; chrono?: number }): JSX.Element {
  return (
    <div className="mb-6 flex items-center gap-3 rounded-2xl bg-white/10 p-4 text-white backdrop-blur sm:gap-4">
      <button
        type="button"
        aria-label="Retour"
        onClick={onRetour}
        className="shrink-0 rounded-lg p-1 text-white/70 hover:text-white"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-bold uppercase tracking-wider text-codialis-mint">Quiz · {quiz.contexte}</div>
        <div className="font-display text-lg font-bold leading-snug">{quiz.titre}</div>
      </div>
      {chrono !== undefined && (
        <div className="shrink-0 text-right" role="timer" aria-label={`Temps restant : ${tempsFormate(chrono)}`}>
          <div className="font-display text-2xl font-extrabold text-codialis-mint">{tempsFormate(chrono)}</div>
          <div className="text-xs text-white/60">temps restant</div>
        </div>
      )}
    </div>
  );
}

function Accueil({ quiz, onRetour }: { quiz: QuizApprenant; onRetour: () => void }): JSX.Element {
  const notifier = useToast();
  const commencer = useCommencerQuiz(quiz.devoirId);
  return (
    <div className="rounded-2xl bg-white p-4 shadow-2xl sm:p-8">
      {quiz.consigne && <p className="mb-5 whitespace-pre-line text-sm leading-relaxed text-codialis-navy">{quiz.consigne}</p>}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-gray-50 p-3">
          <div className="flex items-center gap-1.5 text-xs uppercase text-codialis-gray">
            <ListChecks className="h-4 w-4" aria-hidden /> Questions
          </div>
          <div className="font-display font-bold text-codialis-navy">{quiz.nombreQuestions} QCM</div>
        </div>
        <div className="rounded-xl bg-gray-50 p-3">
          <div className="flex items-center gap-1.5 text-xs uppercase text-codialis-gray">
            <Clock className="h-4 w-4" aria-hidden /> Durée
          </div>
          <div className="font-display font-bold text-codialis-navy">{quiz.dureeMinutes} min</div>
        </div>
        <div className="rounded-xl bg-gray-50 p-3">
          <div className="flex items-center gap-1.5 text-xs uppercase text-codialis-gray">
            <CalendarClock className="h-4 w-4" aria-hidden /> À faire avant
          </div>
          <div className={`font-display font-bold ${quiz.enRetard ? 'text-amber-700' : 'text-codialis-navy'}`}>
            {echeanceLongue(quiz.echeance)}
          </div>
        </div>
      </div>
      <ul className="mb-6 space-y-2 text-sm text-codialis-gray">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
          Une seule tentative : une fois commencé, le quiz ne se recommence pas.
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
          Le temps démarre à « Commencer » et continue de s’écouler si vous quittez la page.
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
          La bonne réponse s’affiche dès qu’une question est validée ou passée ; une question passée compte faux.
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
          Le quiz reste sur la plateforme : son texte ne se copie pas, ne s’imprime pas, ne se télécharge pas.
        </li>
      </ul>
      {quiz.enRetard && !quiz.refusDebut && (
        <p className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" role="note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          L’échéance est passée : le quiz sera signalé en retard.
        </p>
      )}
      {quiz.refusDebut && (
        <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" role="note">
          {quiz.refusDebut}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-3">
        <button type="button" onClick={onRetour} className={BOUTON_SECONDAIRE}>
          Plus tard
        </button>
        <button
          type="button"
          disabled={Boolean(quiz.refusDebut) || commencer.isPending}
          onClick={() =>
            commencer.mutate(undefined, {
              onError: (erreur) => notifier(messageErreur(erreur, 'Le quiz n’a pas pu commencer. Réessayez.')),
            })
          }
          className={BOUTON_PRINCIPAL}
        >
          Commencer le quiz
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function Correction({ question }: { question: QuestionQuizApprenant }): JSX.Element {
  const juste = question.reponse?.correcte ?? false;
  const bonne = question.options.find((option) => option.correcte === true);
  return (
    <div
      role="status"
      className={`rounded-r-lg border-l-4 p-4 ${juste ? 'border-codialis-mint bg-codialis-mint/10' : 'border-amber-500 bg-amber-50'}`}
    >
      <div className={`flex items-center gap-2 font-semibold ${juste ? 'text-codialis-navy' : 'text-amber-800'}`}>
        {juste ? (
          <CheckCircle2 className="h-5 w-5 text-codialis-teal" aria-hidden />
        ) : (
          <XCircle className="h-5 w-5" aria-hidden />
        )}
        {juste
          ? 'Bonne réponse !'
          : `${question.reponse?.optionId ? 'Réponse attendue' : 'Question passée · réponse attendue'} : ${bonne?.lettre ?? '—'}`}
      </div>
      {question.explication && <p className="mt-1 whitespace-pre-line text-sm text-codialis-navy">{question.explication}</p>}
    </div>
  );
}

function QuestionCarte({
  question,
  numero,
  total,
  choix,
  onChoisir,
  onValider,
  onPasser,
  onSuivant,
  estDerniere,
  enAttente,
}: {
  question: QuestionQuizApprenant;
  numero: number;
  total: number;
  choix: string | null;
  onChoisir: (optionId: string) => void;
  onValider: () => void;
  onPasser: () => void;
  onSuivant: () => void;
  estDerniere: boolean;
  enAttente: boolean;
}): JSX.Element {
  const repondue = question.reponse !== null;

  return (
    <div className="mb-6 rounded-2xl bg-white p-4 shadow-2xl sm:p-8">
      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-codialis-mint/15 font-bold text-codialis-teal">
          {numero}
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-codialis-gray">QCM · 1 point</div>
          <h3 className="whitespace-pre-line font-display text-xl font-bold leading-snug text-codialis-navy">{question.enonce}</h3>
        </div>
      </div>

      <fieldset className="mb-6 space-y-3" disabled={repondue}>
        <legend className="sr-only">Réponses de la question {numero}</legend>
        {question.options.map((option) => {
          const selectionnee = repondue ? question.reponse?.optionId === option.id : choix === option.id;
          const bordure = !repondue
            ? selectionnee
              ? 'border-codialis-mint'
              : 'border-gray-200'
            : option.correcte
              ? 'border-codialis-mint'
              : selectionnee
                ? 'border-amber-500'
                : 'border-gray-200';
          return (
            <label
              key={option.id}
              className={`group flex items-start gap-3 rounded-xl border-2 p-4 transition ${repondue ? '' : 'cursor-pointer hover:border-codialis-mint'} ${bordure}`}
            >
              <input
                type="radio"
                name={`quiz-question-${question.id}`}
                value={option.id}
                checked={selectionnee}
                onChange={() => onChoisir(option.id)}
                className="mt-1 accent-codialis-mint"
              />
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-sm font-bold text-codialis-navy group-hover:bg-codialis-mint/20">
                {option.lettre}
              </div>
              <span className="min-w-0 flex-1 whitespace-pre-line pt-1 text-sm text-codialis-navy">{option.texte}</span>
            </label>
          );
        })}
      </fieldset>

      {repondue && (
        <div className="mb-6">
          <Correction question={question} />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {!repondue && (
          <>
            <button type="button" onClick={onPasser} disabled={enAttente} className={BOUTON_SECONDAIRE}>
              Passer
            </button>
            <button type="button" disabled={!choix || enAttente} onClick={onValider} className={BOUTON_PRINCIPAL}>
              Valider ma réponse
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
        {repondue && !estDerniere && (
          <button type="button" onClick={onSuivant} className={BOUTON_PRINCIPAL}>
            Question suivante
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <div className="mt-2 text-right text-xs text-codialis-gray">
        Question {numero} / {total}
      </div>
    </div>
  );
}

function Passage({ quiz, onRetour }: { quiz: QuizApprenant; onRetour: () => void }): JSX.Element {
  const notifier = useToast();
  const repondre = useRepondreQuizQuestion(quiz.devoirId);
  const terminer = useTerminerQuiz(quiz.devoirId);
  const premiereAJouer = Math.max(0, quiz.questions.findIndex((question) => question.reponse === null));
  const [indexCourant, setIndexCourant] = useState(premiereAJouer);
  const [choix, setChoix] = useState<string | null>(null);
  const [tempsRestant, setTempsRestant] = useState(quiz.tentative?.tempsRestantSecondes ?? 0);
  const [confirmation, setConfirmation] = useState(false);

  // Le compte à rebours se cale sur le serveur à chaque relecture, puis
  // décompte localement : le serveur reste seul juge de l'expiration.
  useEffect(() => {
    setTempsRestant(quiz.tentative?.tempsRestantSecondes ?? 0);
  }, [quiz.tentative?.tempsRestantSecondes]);

  useEffect(() => {
    if (tempsRestant <= 0) {
      if (!terminer.isPending) {
        terminer.mutate(undefined, {
          onError: (erreur) => notifier(messageErreur(erreur, 'Le quiz n’a pas pu être clôturé.')),
        });
      }
      return;
    }
    const minuteur = setTimeout(() => setTempsRestant((valeur) => valeur - 1), 1000);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempsRestant]);

  const question = quiz.questions[indexCourant];
  if (!question) return <MessageErreur message="Cette question n’existe pas." />;

  const nombreJouees = quiz.questions.filter((une) => une.reponse !== null).length;
  const pourcentage = Math.round((nombreJouees / quiz.nombreQuestions) * 100);
  const restantes = quiz.nombreQuestions - nombreJouees;

  const allerA = (index: number): void => {
    setIndexCourant(index);
    setChoix(null);
  };
  const jouer = (optionId: string | null): void => {
    repondre.mutate(
      { questionId: question.id, optionId },
      { onError: (erreur) => notifier(messageErreur(erreur, 'Votre réponse n’a pas pu être enregistrée.')) },
    );
  };

  return (
    <>
      <EnTete quiz={quiz} onRetour={onRetour} chrono={Math.max(0, tempsRestant)} />

      <div className="mb-6 flex items-center gap-3 rounded-xl bg-white/10 p-3 text-white backdrop-blur">
        <span className="whitespace-nowrap text-sm font-medium">
          Question <strong>{indexCourant + 1}</strong> / {quiz.nombreQuestions}
        </span>
        <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-codialis-mint transition-all" style={{ width: `${pourcentage}%` }} />
        </div>
        <span className="text-sm font-semibold text-codialis-mint">{pourcentage}%</span>
      </div>

      <QuestionCarte
        key={question.id}
        question={question}
        numero={indexCourant + 1}
        total={quiz.nombreQuestions}
        choix={choix}
        onChoisir={setChoix}
        onValider={() => choix && jouer(choix)}
        onPasser={() => jouer(null)}
        onSuivant={() => {
          const suivante = quiz.questions.findIndex((une, rang) => rang > indexCourant && une.reponse === null);
          allerA(suivante >= 0 ? suivante : Math.min(indexCourant + 1, quiz.questions.length - 1));
        }}
        estDerniere={indexCourant === quiz.questions.length - 1}
        enAttente={repondre.isPending}
      />

      <div className="rounded-xl bg-white/10 p-4 backdrop-blur">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-bold uppercase tracking-wide text-white/80">Navigation</div>
          <button
            type="button"
            onClick={() => setConfirmation(true)}
            disabled={terminer.isPending}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-white/15 px-3 text-xs font-semibold text-white hover:bg-white/25 disabled:opacity-60"
          >
            <Flag className="h-3.5 w-3.5" aria-hidden />
            Terminer le quiz
          </button>
        </div>
        <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
          {quiz.questions.map((une, index) => {
            const jouee = une.reponse !== null;
            const classe =
              index === indexCourant
                ? 'bg-white text-codialis-navy ring-2 ring-codialis-mint'
                : jouee
                  ? 'bg-codialis-mint text-codialis-navy'
                  : 'bg-white/10 text-white/60 hover:bg-white/20';
            return (
              <button
                key={une.id}
                type="button"
                aria-label={`Question ${index + 1}${jouee ? ', jouée' : ''}`}
                aria-current={index === indexCourant ? 'true' : undefined}
                onClick={() => allerA(index)}
                className={`aspect-square rounded-lg text-sm font-bold transition ${classe}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-white/70">
          <div className="flex flex-wrap gap-4">
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-codialis-mint" />
              Jouée
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-white" />
              En cours
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded bg-white/20" />
              À venir
            </span>
          </div>
          <span>
            {nombreJouees} jouée{nombreJouees > 1 ? 's' : ''} · {restantes} restante{restantes > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <Modale titre="Terminer le quiz ?" ouverte={confirmation} onFermer={() => setConfirmation(false)} largeur="max-w-md">
        <div className="p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            {restantes > 0
              ? `${restantes} question${restantes > 1 ? 's' : ''} non jouée${restantes > 1 ? 's' : ''} compter${restantes > 1 ? 'ont' : 'a'} faux. `
              : ''}
            Le quiz ne se recommence pas.
          </p>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setConfirmation(false)} className={BOUTON_SECONDAIRE}>
              Continuer le quiz
            </button>
            <button
              type="button"
              disabled={terminer.isPending}
              onClick={() =>
                terminer.mutate(undefined, {
                  onSuccess: () => setConfirmation(false),
                  onError: (erreur) => notifier(messageErreur(erreur, 'Le quiz n’a pas pu être clôturé.')),
                })
              }
              className={BOUTON_PRINCIPAL}
            >
              Terminer
            </button>
          </div>
        </div>
      </Modale>
    </>
  );
}

function Resultat({ quiz, onRetour }: { quiz: QuizApprenant; onRetour: () => void }): JSX.Element {
  const resultat = quiz.resultat!;
  const correction = quiz.correction;
  return (
    <div className="rounded-2xl bg-white p-4 shadow-2xl sm:p-8">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-codialis-mint/15 text-codialis-teal">
          <CheckCircle2 className="h-8 w-8" aria-hidden />
        </div>
        <h2 className="font-display text-2xl font-bold text-codialis-navy">{correction ? 'Quiz corrigé' : 'Quiz terminé'}</h2>
        <div className="mt-6 flex flex-wrap items-end justify-center gap-x-8 gap-y-2">
          <div>
            <div className="font-display text-4xl font-extrabold text-codialis-navy">
              {noteLisible(correction ? correction.note : resultat.note)}
              <span className="text-xl text-codialis-gray">/20</span>
            </div>
            <div className="text-xs uppercase text-codialis-gray">{correction ? 'Note de votre formateur' : 'Note automatique'}</div>
          </div>
          <div>
            <div className="font-display text-2xl font-bold text-codialis-navy">
              {resultat.bonnes} / {resultat.total}
            </div>
            <div className="text-xs uppercase text-codialis-gray">{resultat.pourcentage} % de bonnes réponses</div>
          </div>
        </div>
        {!correction && (
          <p className="mx-auto mt-4 max-w-md text-xs text-codialis-gray">
            Note provisoire : votre formateur la valide ou l’ajuste, puis publie sa correction.
          </p>
        )}
      </div>

      {correction && (
        <section className="mt-6 rounded-xl border border-codialis-mint/30 bg-codialis-mint/5 p-4">
          <h3 className="font-display font-bold text-codialis-navy">
            Correction{correction.correcteur ? ` de ${correction.correcteur}` : ''}
          </h3>
          <p className="mt-2 whitespace-pre-line break-words text-sm text-codialis-navy">
            {correction.commentaire ?? 'Pas de commentaire.'}
          </p>
          <p className="mt-2 text-xs text-codialis-gray">
            Note automatique : {noteLisible(resultat.note)}/20
            {correction.rectifieeLe ? ' · note modifiée depuis la publication' : ''}
          </p>
        </section>
      )}

      <h3 className="mb-3 mt-8 font-display font-bold text-codialis-navy">Correction des questions</h3>
      <ol className="space-y-3">
        {quiz.questions.map((question, index) => {
          const juste = question.reponse?.correcte ?? false;
          const donnee = question.options.find((option) => option.id === question.reponse?.optionId);
          const bonne = question.options.find((option) => option.correcte === true);
          return (
            <li key={question.id} className="rounded-xl border border-gray-100 p-3 text-sm">
              <div className="flex items-start gap-3">
                {juste ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-teal" aria-label="Juste" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-label="Fausse" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="whitespace-pre-line font-medium text-codialis-navy">
                    {index + 1}. {question.enonce}
                  </div>
                  <div className="mt-1 text-xs text-codialis-gray">
                    Votre réponse : {donnee ? `${donnee.lettre} · ${donnee.texte}` : question.reponse ? 'passée' : 'non jouée'}
                  </div>
                  {!juste && bonne && (
                    <div className="text-xs text-codialis-navy">
                      Bonne réponse : {bonne.lettre} · {bonne.texte}
                    </div>
                  )}
                  {question.explication && <p className="mt-1 whitespace-pre-line text-xs text-codialis-gray">{question.explication}</p>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-8 flex justify-center">
        <button type="button" onClick={onRetour} className={BOUTON_PRINCIPAL}>
          <ChevronLeft className="h-4 w-4" aria-hidden />
          Retour
        </button>
      </div>
    </div>
  );
}

export function QuizPage(): JSX.Element {
  const naviguer = useNavigate();
  const [parametres] = useSearchParams();
  const { devoirId = '' } = useParams<{ devoirId: string }>();
  const { data: quiz, isLoading, error } = useQuizApprenant(devoirId);
  const retour = retourDemande(parametres.get('retour'));
  const revenir = (): void => naviguer(retour);

  if (isLoading) return <Chargement libelle="Chargement du quiz…" />;
  if (error || !quiz) {
    return <MessageErreur message={messageErreur(error, 'Ce quiz n’a pas pu être chargé.')} />;
  }

  return (
    <div className={FOND}>
      <div className="mx-auto max-w-3xl">
        <ContenuProtege libelle="Ce quiz">
          {quiz.resultat ? (
            <>
              <EnTete quiz={quiz} onRetour={revenir} />
              <Resultat quiz={quiz} onRetour={revenir} />
            </>
          ) : quiz.tentative ? (
            <Passage quiz={quiz} onRetour={revenir} />
          ) : (
            <>
              <EnTete quiz={quiz} onRetour={revenir} />
              <Accueil quiz={quiz} onRetour={revenir} />
            </>
          )}
        </ContenuProtege>
      </div>
    </div>
  );
}
